"use client";

import { useCallback, useMemo, useState } from "react";
import { useRouter } from "next/navigation";
import { CornerDownLeft, Loader2 } from "lucide-react";
import { useGlobalModalStack, ResponsiveModal } from "@wtc/ui";
import {
  Command,
  CommandInput,
  CommandList,
  CommandShortcut,
} from "@/components/ui/command";
import type { SearchResult } from "@/lib/hooks/algolia-search/types";
import type {
  CreateAction,
  CreateActionGateContext,
} from "@/components/ui/navigation/enhanced-quick-actions/create-actions";
import type { NavigationItem } from "@/components/ui/navigation/types";
import { filterDestinations, flattenNavigationForPalette } from "./flatten-navigation";
import { useCommandPalette } from "./command-palette-context";
import { filterCreateActions, resolveCreateActions } from "./palette-create-actions";
import { MIN_RECORD_QUERY_LENGTH, usePaletteRecordSearch } from "./usePaletteRecordSearch";
import {
  CreateActionsGroup,
  DestinationsGroup,
  FullSearchGroup,
  RecordResultsGroup,
} from "./CommandPaletteGroups";

interface CommandPaletteProps {
  /** Role-filtered navigation tree — the palette never offers a hidden page. */
  navigationItems: readonly NavigationItem[];
  /** Gating inputs for the create catalog, shared with the quick-create menu. */
  createGateContext: CreateActionGateContext;
  /** Clients get exactly one create action (CLIENT_USER_ACCESS_SCOPE). */
  clientRestricted?: boolean;
  /** Run a record search. Resolves to whatever the search layer returns. */
  onSearchRecords: (query: string) => Promise<SearchResult[]>;
}

/**
 * Hub's ⌘K command palette: jump to a page, create a record, or find a record,
 * without leaving the keyboard.
 *
 * Everything it offers is derived from existing sources — the role-filtered nav
 * tree and the shared create catalog — so it cannot drift from the menus, and
 * it cannot expose a destination or action the current user lacks.
 *
 * The pieces live alongside this file: `flatten-navigation` (destinations),
 * `palette-create-actions` (create catalog + its filter),
 * `usePaletteRecordSearch` (debounced server search), `CommandPaletteGroups`
 * (the rendered groups).
 */
export function CommandPalette({
  navigationItems,
  createGateContext,
  clientRestricted = false,
  onSearchRecords,
}: CommandPaletteProps) {
  const router = useRouter();
  const { openModal } = useGlobalModalStack();
  const { open, setOpen, shortcutLabel } = useCommandPalette();

  const [query, setQuery] = useState("");
  const trimmedQuery = query.trim();

  const destinations = useMemo(
    () => flattenNavigationForPalette(navigationItems),
    [navigationItems]
  );

  const createActions = useMemo(
    () => resolveCreateActions(createGateContext, clientRestricted),
    [createGateContext, clientRestricted]
  );

  // cmdk's own filtering is OFF (see the Command tag below), so both of these
  // narrow explicitly against what was typed.
  const visibleDestinations = useMemo(
    () => filterDestinations(destinations, trimmedQuery),
    [destinations, trimmedQuery]
  );
  const visibleCreateActions = useMemo(
    () => filterCreateActions(createActions, trimmedQuery),
    [createActions, trimmedQuery]
  );

  const { records, isSearching } = usePaletteRecordSearch(open, trimmedQuery, onSearchRecords);

  // The palette closes before it navigates, so a route change never happens
  // underneath an open modal.
  const runAndClose = useCallback(
    (run: () => void) => {
      setOpen(false);
      run();
    },
    [setOpen]
  );

  const handleCreateAction = useCallback(
    (action: CreateAction) => {
      runAndClose(() => {
        if (action.action.type === "modal") {
          openModal(action.action.modalId);
          return;
        }
        router.push(action.action.href);
      });
    },
    [runAndClose, openModal, router]
  );

  const hasAnyResult =
    visibleDestinations.length > 0 || visibleCreateActions.length > 0 || records.length > 0;

  return (
    // ResponsiveModal, not a raw Dialog: Hub's modal system owns the mobile
    // presentation (a top sheet here rather than a centered box on a phone) and
    // the global modal stack — see RESPONSIVE_MODAL_SYSTEM.
    <ResponsiveModal
      open={open}
      onOpenChange={setOpen}
      title="Search and commands"
      description="Search Hub, jump to a page, or create a record."
      maxWidth="2xl"
      side="top"
      className="overflow-hidden p-0"
    >
      {/*
          shouldFilter={false}: record results are already filtered SERVER-SIDE
          against fields cmdk cannot see (an email address, a company domain),
          so letting cmdk re-filter them by visible label would hide valid
          matches. The other two groups are filtered explicitly against `query`
          — `visibleDestinations` via filterDestinations() and
          `visibleCreateActions` via filterCreateActions() — so nothing on
          screen ignores what was typed.
        */}
      {/* validate-command-shouldfilter-allow: records filtered server-side by query; destinations/create filtered explicitly above */}
      <Command shouldFilter={false} className="[&_[cmdk-group-heading]]:px-3">
        <CommandInput
          value={query}
          onValueChange={setQuery}
          placeholder="Search contacts, companies, deals, tasks, and email — or jump to a page…"
        />

        <CommandList className="max-h-[min(28rem,70vh)]">
          {/*
              A plain row, NOT cmdk's <CommandEmpty>: CommandEmpty renders only
              when the Command has ZERO items, and the "See all results" row
              below is always present once anything is typed — so as
              CommandEmpty this message could never appear.
            */}
          {!hasAnyResult && !isSearching && trimmedQuery.length > 0 && (
            <div className="px-4 py-6 text-center text-sm text-muted-foreground">
              No matches for &ldquo;{trimmedQuery}&rdquo;.
            </div>
          )}

          <RecordResultsGroup
            records={records}
            onSelect={(result) => runAndClose(() => router.push(result.url))}
          />

          {isSearching && records.length === 0 && trimmedQuery.length >= MIN_RECORD_QUERY_LENGTH && (
            <div className="flex items-center gap-2 px-4 py-3 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              Searching records…
            </div>
          )}

          <DestinationsGroup
            destinations={visibleDestinations}
            separated={records.length > 0}
            onSelect={(destination) => runAndClose(() => router.push(destination.href))}
          />

          <CreateActionsGroup actions={visibleCreateActions} onSelect={handleCreateAction} />

          <FullSearchGroup
            trimmedQuery={trimmedQuery}
            onSelect={() =>
              runAndClose(() => router.push(`/search?q=${encodeURIComponent(trimmedQuery)}`))
            }
          />
        </CommandList>

        {/* Footer keeps the shortcut visible, so the palette teaches itself. */}
        <div className="flex items-center justify-between border-t border-border px-3 py-2 text-[11px] text-muted-foreground">
          <span className="flex items-center gap-1.5">
            <CornerDownLeft className="h-3 w-3" aria-hidden />
            to open
          </span>
          <CommandShortcut className="text-[11px]">{shortcutLabel} to toggle</CommandShortcut>
        </div>
      </Command>
    </ResponsiveModal>
  );
}
